import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  InboxIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  DocumentIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const variantConfig = {
  default: {
    icon: InboxIcon,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-400',
    title: 'Data Kosong',
    subtitle: 'Belum ada data untuk ditampilkan saat ini.',
  },
  search: {
    icon: MagnifyingGlassIcon,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-400',
    title: 'Data tidak ditemukan',
    subtitle: 'Coba ubah filter atau kata kunci pencarian Anda.',
  },
  error: {
    icon: ExclamationCircleIcon,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-400',
    title: 'Terjadi Kesalahan',
    subtitle: 'Gagal memuat data. Silakan coba lagi.',
  },
  document: {
    icon: DocumentIcon,
    iconBg: 'bg-primary-light',
    iconColor: 'text-primary',
    title: 'Belum Ada Data',
    subtitle: 'Tambahkan data baru untuk mulai.',
  },
};

const EmptyState = ({
  variant = 'default',
  title,
  subtitle,
  actionLabel,
  onAction,
  className = '',
  animated = true,
}) => {
  const reduceMotion = useReducedMotion();
  const config = variantConfig[variant] || variantConfig.default;
  const Icon = config.icon;

  const content = (
    <div
      className={`flex flex-col items-center justify-center py-12 px-6 text-center ${className}`}
      role='status'
      aria-live='polite'
    >
      <div
        className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ${config.iconBg} mb-5 shadow-sm`}
      >
        <Icon className={`h-10 w-10 ${config.iconColor}`} aria-hidden='true' />
      </div>
      <h3 className='text-lg font-semibold text-gray-700 mb-1'>{title || config.title}</h3>
      <p className='text-sm text-gray-400 max-w-sm leading-relaxed'>
        {subtitle || config.subtitle}
      </p>
      {actionLabel && onAction && (
        <motion.button
          type='button'
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onAction}
          className='mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/20 hover:bg-primary-dark transition-colors'
        >
          <PlusIcon className='h-4 w-4' />
          {actionLabel}
        </motion.button>
      )}
    </div>
  );

  if (!animated || reduceMotion) return content;

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        key={variant}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
      >
        {content}
      </motion.div>
    </AnimatePresence>
  );
};

export default EmptyState;
